import React, { useState } from "react";
import { formatUserHandle } from "../../../utils/userHandle";
import { useConfirm } from "../../../ui/components/ConfirmDialog";
import {
  useAddAdminParticipantMutation,
  useGetAdminParticipantsQuery,
  useRemoveAdminParticipantMutation,
} from "../api/gameApi";
import type { AdminParticipantRow, AdminSeason } from "../api/types";
import { errorText, fmtMoment, reachText } from "./adminText";
import { Btn, Input, Label, Notice, Pill, Row, Section, SectionTitle, Small, Table, TableWrap } from "./adminStyles";

function frameMark(r: AdminParticipantRow): string | null {
  if (r.gameFrame) return "game's frame";
  if (r.frameFromLeaderboard) return "leaderboard frame";
  return null;
}

/** Why players cannot be added right now, or null when they can (the backend refuses the same phases). */
function addClosedReason(phase: AdminSeason["phase"]): string | null {
  switch (phase) {
    case "draft":
      return "Players can be added once the season is announced. A draft is hidden, and adding someone would tell them and the sitebot about it.";
    case "ended":
      return "The season is over, so nobody can be added.";
    case "archived":
      return "The season is archived, so its players stay as they are.";
    default:
      return null;
  }
}

/** Who is in the game, how they stand, and whether the game can reach them. */
const ParticipantsPanel: React.FC<{ season: AdminSeason }> = ({ season }) => {
  const confirm = useConfirm();
  const { data, isLoading, isError, error: loadError, refetch } = useGetAdminParticipantsQuery(season.id);
  const [add, { isLoading: adding }] = useAddAdminParticipantMutation();
  const [remove, { isLoading: removing }] = useRemoveAdminParticipantMutation();
  const [username, setUsername] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const closedReason = addClosedReason(season.phase);
  const canRemove = season.phase !== "archived";

  const addPlayer = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = username.trim();
    if (!name) return;
    setError(null);
    setDone(null);
    try {
      const res = await add({ id: season.id, username: name }).unwrap();
      setDone(res.inserted ? `Added ${name}.` : `${name} was already in the game.`);
      setUsername("");
    } catch (err) {
      setError(errorText(err, "Could not add the player."));
    }
  };

  const removePlayer = async (r: AdminParticipantRow) => {
    setError(null);
    setDone(null);
    const handle = formatUserHandle(r.username, r.emoji);
    const ok = await confirm({
      title: `Remove ${handle}?`,
      message: `${handle} leaves the game. Their points for every day are deleted and the standings are rebuilt. They can join again from /game while joining is open.`,
      confirmLabel: "Remove",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await remove({ id: season.id, username: r.username }).unwrap();
      setDone(`Removed ${r.username}.`);
    } catch (err) {
      setError(errorText(err, "Could not remove the player."));
    }
  };

  return (
    <Section aria-label="Players">
      <SectionTitle>Players{data ? ` (${data.count})` : ""}</SectionTitle>
      {closedReason ? (
        <Row>
          <Small style={{ flex: "1 1 200px" }}>{closedReason}</Small>
          <Btn type="button" onClick={() => void refetch()}>
            Refresh
          </Btn>
        </Row>
      ) : (
        <form onSubmit={addPlayer}>
          <Row>
            <Label style={{ flex: "1 1 200px" }}>
              Add a player by username (no notifications check)
              <Input
                aria-label="Username to add"
                value={username}
                autoCapitalize="none"
                autoCorrect="off"
                onChange={(e) => setUsername(e.target.value)}
              />
            </Label>
            <Btn type="submit" variant="primary" disabled={adding || !username.trim()}>
              {adding ? "Adding…" : "Add"}
            </Btn>
            <Btn type="button" onClick={() => void refetch()}>
              Refresh
            </Btn>
          </Row>
        </form>
      )}
      {error && (
        <Notice tone="error" role="alert">
          {error}
        </Notice>
      )}
      {done && (
        <Notice tone="ok" role="status">
          {done}
        </Notice>
      )}

      {isLoading && <Small>Loading players…</Small>}
      {isError && (
        <Notice tone="error" role="alert">
          {errorText(loadError, "Could not load the players.")}
        </Notice>
      )}
      {data && !data.reachAvailable && (
        <Notice tone="warn">Messenger did not answer, so reach is unknown right now.</Notice>
      )}
      {data && data.rows.length === 0 && <Small>Nobody has joined yet.</Small>}
      {data && data.rows.length > 0 && (
        <TableWrap>
          <Table aria-label="Players">
            <thead>
              <tr>
                <th>#</th>
                <th>Player</th>
                <th>Points</th>
                <th>Raced</th>
                <th>Race wins</th>
                <th>Guessing wins</th>
                <th>Reach</th>
                <th>Joined</th>
                {canRemove && <th aria-label="Remove" />}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => {
                const reach = reachText(r.reach);
                const mark = frameMark(r);
                return (
                  <tr key={r.username} data-username={r.username}>
                    <td>{r.rank ?? "-"}</td>
                    <td>
                      {formatUserHandle(r.username, r.emoji)}
                      {mark && (
                        <>
                          {" "}
                          <Pill tone="info">{mark}</Pill>
                        </>
                      )}
                    </td>
                    <td>
                      <strong>{r.total}</strong>
                    </td>
                    <td>{r.racedDays}</td>
                    <td>{r.wins}</td>
                    <td>{r.guessWins}</td>
                    <td>
                      <Pill tone={reach.tone}>{reach.text}</Pill>
                    </td>
                    <td>{fmtMoment(r.joinedAt)}</td>
                    {canRemove && (
                      <td>
                        <Btn
                          type="button"
                          variant="danger"
                          aria-label={`Remove ${r.username}`}
                          disabled={removing}
                          onClick={() => void removePlayer(r)}
                        >
                          Remove
                        </Btn>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </TableWrap>
      )}
      {data?.reachCheckedAt && <Small style={{ marginTop: 8 }}>Reach checked {fmtMoment(data.reachCheckedAt)}.</Small>}
    </Section>
  );
};

export default ParticipantsPanel;
